/** Helpers puros de gravação/preview de áudio (MediaRecorder). */

export function formatRecordingTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function isValidMediaDuration(d: number): boolean {
    return Number.isFinite(d) && d > 0 && d !== Infinity;
}

export function stopMediaTracks(stream: MediaStream): void {
    stream.getTracks().forEach(track => track.stop());
}

export function stopMediaRecorderQuietly(recorder: MediaRecorder): void {
    if (recorder.state === 'inactive') return;
    try {
        recorder.stop();
    } catch {
        // ignore
    }
}

export function seekAudioSafe(audio: HTMLAudioElement, time: number): void {
    try {
        audio.currentTime = time;
    } catch {
        // ignore — blob incompleto / seek inválido
    }
}

export function pickRecorderMime(): { mimeType: string; type: string; ext: string } {
    // iOS Safari: mp4/AAC. Chrome: webm/opus. Ogg quando disponível (Firefox).
    const candidates: Array<{ mimeType: string; type: string; ext: string }> = [
        { mimeType: 'audio/mp4', type: 'audio/mp4', ext: 'm4a' },
        { mimeType: 'audio/mp4;codecs=mp4a.40.2', type: 'audio/mp4', ext: 'm4a' },
        { mimeType: 'audio/aac', type: 'audio/aac', ext: 'aac' },
        { mimeType: 'audio/ogg;codecs=opus', type: 'audio/ogg', ext: 'ogg' },
        { mimeType: 'audio/webm;codecs=opus', type: 'audio/webm', ext: 'webm' },
    ];
    for (const c of candidates) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c.mimeType)) {
            return c;
        }
    }
    return { mimeType: '', type: 'audio/mp4', ext: 'm4a' };
}

export function extForAudioMime(mimeType: string): string {
    const mime = (mimeType || '').toLowerCase().split(';')[0].trim();
    if (mime.includes('mp4') || mime.includes('m4a') || mime.includes('aac')) return 'm4a';
    if (mime.includes('ogg') || mime.includes('opus')) return 'ogg';
    if (mime.includes('webm')) return 'webm';
    if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
    return 'm4a';
}

/** Cria MediaRecorder com fallback se o mime preferido não for aceito. */
export function createAudioRecorder(stream: MediaStream): { recorder: MediaRecorder; type: string; ext: string } {
    const picked = pickRecorderMime();
    let recorder: MediaRecorder;
    try {
        recorder = picked.mimeType
            ? new MediaRecorder(stream, { mimeType: picked.mimeType })
            : new MediaRecorder(stream);
    } catch {
        recorder = new MediaRecorder(stream);
    }
    const type = (recorder.mimeType || picked.type || 'audio/mp4').split(';')[0];
    return { recorder, type, ext: extForAudioMime(type) };
}

export function resolveAudioDuration(
    audio: HTMLAudioElement,
    storedDuration: number,
    fallbackSeconds: number,
): number {
    if (isValidMediaDuration(audio.duration)) return audio.duration;
    if (isValidMediaDuration(storedDuration)) return storedDuration;
    return Math.max(fallbackSeconds, 0.001);
}

/** Limpa elemento + revoga URL. Caller zera as refs. */
export function teardownPreviewAudio(
    audio: HTMLAudioElement | null,
    objectUrl: string | null,
): void {
    if (audio) {
        audio.onloadedmetadata = null;
        audio.ontimeupdate = null;
        audio.onended = null;
        audio.onerror = null;
        try {
            audio.pause();
        } catch {
            // ignore
        }
        audio.removeAttribute('src');
        try {
            audio.load();
        } catch {
            // ignore
        }
    }
    if (objectUrl) URL.revokeObjectURL(objectUrl);
}

export function buildPreviewObjectUrl(chunks: Blob[], mimeType: string): string | null {
    if (!chunks.length) return null;
    const blob = new Blob(chunks, { type: mimeType });
    if (!blob.size) return null;
    return URL.createObjectURL(blob);
}

/** Para o recorder sem disparar onstop (descarte / unmount). */
export function discardMediaRecorder(recorder: MediaRecorder | null): void {
    if (!recorder) return;
    recorder.onstop = null;
    recorder.ondataavailable = null;
    stopMediaRecorderQuietly(recorder);
    stopMediaTracks(recorder.stream);
}

export function buildRecordingFile(
    chunks: Blob[],
    mimeType: string,
    ext: string,
): File | null {
    const blob = new Blob(chunks, { type: mimeType });
    if (!blob.size) return null;
    return new File([blob], `audio_${Date.now()}.${ext}`, { type: mimeType });
}

/** Voz enviada à Uazapi: WAV PCM 16 kHz mono (a API converte para OPUS). */
const VOICE_SAMPLE_RATE = 16_000;

function mixToMono(buffer: AudioBuffer): Float32Array {
    const length = buffer.length;
    const channels = buffer.numberOfChannels;
    const mixed = new Float32Array(length);
    for (let channel = 0; channel < channels; channel++) {
        const data = buffer.getChannelData(channel);
        for (let i = 0; i < length; i++) mixed[i] += data[i] / channels;
    }
    return mixed;
}

function resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
    if (fromRate === toRate || input.length === 0) return input;
    const outLength = Math.max(1, Math.round((input.length * toRate) / fromRate));
    const output = new Float32Array(outLength);
    const ratio = fromRate / toRate;
    for (let i = 0; i < outLength; i++) {
        const position = i * ratio;
        const left = Math.floor(position);
        const right = Math.min(left + 1, input.length - 1);
        const weight = position - left;
        output[i] = input[left] * (1 - weight) + input[right] * weight;
    }
    return output;
}

function encodeWavPcm16(samples: Float32Array, sampleRate: number): Blob {
    const bytes = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(bytes);
    const write = (offset: number, text: string) => {
        for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
    };
    write(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    write(8, 'WAVE');
    write(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    write(36, 'data');
    view.setUint32(40, samples.length * 2, true);
    let offset = 44;
    for (let i = 0; i < samples.length; i++) {
        const sample = Math.max(-1, Math.min(1, samples[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
    }
    return new Blob([bytes], { type: 'audio/wav' });
}

/**
 * Junta os trechos gravados num WAV contínuo.
 * Cada trecho precisa ser um container válido (um stop do MediaRecorder).
 * Retorna null se o browser não conseguir decodificar — o caller usa o blob original.
 */
export async function blobsToVoiceFile(blobs: Blob[]): Promise<File | null> {
    const parts = blobs.filter((blob) => blob.size > 0);
    if (!parts.length || typeof AudioContext === 'undefined') return null;

    const context = new AudioContext();
    try {
        const pieces: Float32Array[] = [];
        for (const blob of parts) {
            const encoded = await blob.arrayBuffer();
            const buffer = await context.decodeAudioData(encoded.slice(0));
            const mono = mixToMono(buffer);
            pieces.push(resampleLinear(mono, buffer.sampleRate || context.sampleRate, VOICE_SAMPLE_RATE));
        }
        const total = pieces.reduce((sum, piece) => sum + piece.length, 0);
        if (!total) return null;
        const merged = new Float32Array(total);
        let offset = 0;
        for (const piece of pieces) {
            merged.set(piece, offset);
            offset += piece.length;
        }
        const wav = encodeWavPcm16(merged, VOICE_SAMPLE_RATE);
        return new File([wav], `audio_${Date.now()}.wav`, { type: 'audio/wav' });
    } catch {
        return null;
    } finally {
        await context.close().catch(() => undefined);
    }
}
